import { Extension } from "@tiptap/core";
import { Plugin } from "@tiptap/pm/state";

const isMarkdownContent = (text: string) => {
  const tablePattern = /^\s*\|.+\|/m;
  const headerPattern = /^#{1,6}\s/m;
  const listPattern = /^(\s*[-*]|\s*\d+\.)\s/m;

  return (
    tablePattern.test(text) ||
    headerPattern.test(text) ||
    listPattern.test(text)
  );
};

const normalizeClipboardText = (text: string) =>
  text.replace(/\r\n?/g, "\n").trim();

const clipboardLineElements = new Set([
  "ADDRESS",
  "ARTICLE",
  "ASIDE",
  "BLOCKQUOTE",
  "DIV",
  "FOOTER",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "HEADER",
  "LI",
  "MAIN",
  "NAV",
  "OL",
  "P",
  "PRE",
  "SECTION",
  "TABLE",
  "TR",
  "UL",
]);

const readClipboardHtmlText = (node: ChildNode): string => {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent ?? "";
  }

  if (node.nodeName === "BR") {
    return "\n";
  }

  const text = Array.from(node.childNodes).map(readClipboardHtmlText).join("");

  return clipboardLineElements.has(node.nodeName) && !text.endsWith("\n")
    ? `${text}\n`
    : text;
};

const doesClipboardHtmlMatchText = (html: string, plainText: string) => {
  const clipboardDocument = new DOMParser().parseFromString(html, "text/html");
  const htmlText = Array.from(clipboardDocument.body.childNodes)
    .filter(
      (node) =>
        node.nodeType !== Node.TEXT_NODE || Boolean(node.textContent?.trim())
    )
    .map(readClipboardHtmlText)
    .join("");

  return normalizeClipboardText(htmlText) === normalizeClipboardText(plainText);
};

export const MarkdownPaste = Extension.create({
  name: "markdownPaste",

  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          handlePaste: (_view, event) => {
            const clipboardData = event.clipboardData;

            if (!clipboardData) {
              return false;
            }

            const text = clipboardData.getData("text/plain");

            if (!text || !isMarkdownContent(text)) {
              return false;
            }

            const html = clipboardData.getData("text/html");

            if (html && !doesClipboardHtmlMatchText(html, text)) {
              return false;
            }

            const markdownContent = this.editor.markdown?.parse(text).content;

            if (!markdownContent?.length) {
              return false;
            }

            const didInsert = this.editor
              .chain()
              .insertContent(markdownContent)
              .setMeta("paste", true)
              .setMeta("uiEvent", "paste")
              .scrollIntoView()
              .run();

            if (didInsert) {
              event.preventDefault();
            }

            return didInsert;
          },
        },
      }),
    ];
  },
});
